import { useEffect, useState } from "react";
import { Plus, Pencil, Star } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, formatPrice, imgSrc, STATUS, TYPES } from "@/lib/api";
import { ProgrammeFormDialog } from "@/components/admin/ProgrammeFormDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

export const AdminProgrammes = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/programmes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (p, patch) => {
    try {
      await api.put(`/programmes/${p.id}`, { ...p, ...patch });
      toast.success("Programme mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/programmes/${p.id}`);
      toast.success("Programme supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-programmes">
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <h1 className="font-display text-4xl">Programmes & biens</h1>
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C88A58] hover:bg-[#b57849] text-white rounded-full text-sm transition-colors" data-testid="admin-add-programme-button">
          <Plus className="h-4 w-4" /> Nouveau programme
        </button>
      </div>
      <div className="mt-8 space-y-4 md:hidden" data-testid="admin-programmes-mobile-list">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-[#2F2A28]/10 rounded-sm overflow-hidden" data-testid={`admin-programme-card-${p.id}`}>
            {p.images[0] && <img src={imgSrc(p.images[0])} alt="" className="h-40 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-[#756D69]">{p.city} · {TYPES[p.type]} · {p.images.length} photo{p.images.length > 1 ? "s" : ""}</p>
                  <p className="text-sm text-[#C88A58] mt-1">{formatPrice(p.price, p.price_from)}</p>
                </div>
                <button onClick={() => save(p, { featured: !p.featured })} data-testid={`admin-mobile-toggle-featured-${p.id}`}>
                  <Star className={`h-5 w-5 ${p.featured ? "fill-[#C88A58] text-[#C88A58]" : "text-[#756D69]"}`} />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setEditing(p)} className="flex-1 inline-flex items-center justify-center gap-2 h-11 bg-[#2F2A28] text-white rounded-full text-sm" data-testid={`admin-mobile-edit-programme-${p.id}`}>
                  <Pencil className="h-4 w-4" /> Modifier / Photos
                </button>
                <ConfirmDelete title={`Supprimer « ${p.name} » ?`} onConfirm={() => remove(p)} testId={`admin-mobile-delete-programme-${p.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 bg-white border border-[#2F2A28]/10 rounded-sm overflow-x-auto hidden md:block">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-left text-xs uppercase tracking-wider text-[#756D69] border-b border-[#2F2A28]/10">
            <tr><th className="p-4">Programme</th><th className="p-4">Type</th><th className="p-4">Prix</th><th className="p-4">Statut</th><th className="p-4">Vedette</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b last:border-0 border-[#2F2A28]/5" data-testid={`admin-programme-row-${p.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.images[0] ? <img src={imgSrc(p.images[0])} alt="" className="h-12 w-16 object-cover rounded-sm" /> : <div className="h-12 w-16 bg-[#F2EDE4] rounded-sm" />}
                    <div><p className="font-medium">{p.name}</p><p className="text-xs text-[#756D69]">{p.city}</p></div>
                  </div>
                </td>
                <td className="p-4">{TYPES[p.type]}</td>
                <td className="p-4">{formatPrice(p.price, p.price_from)}</td>
                <td className="p-4">
                  <Select value={p.status} onValueChange={(v) => save(p, { status: v })}>
                    <SelectTrigger className="w-40 h-9" data-testid={`admin-toggle-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <button onClick={() => save(p, { featured: !p.featured })} data-testid={`admin-toggle-featured-${p.id}`}>
                    <Star className={`h-5 w-5 ${p.featured ? "fill-[#C88A58] text-[#C88A58]" : "text-[#756D69]"}`} />
                  </button>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#2F2A28]/15 rounded-full text-xs hover:border-[#C88A58] hover:text-[#C88A58]" data-testid={`admin-edit-programme-${p.id}`}><Pencil className="h-3.5 w-3.5" /> Modifier / Photos</button>
                  <ConfirmDelete title={`Supprimer « ${p.name} » ?`} onConfirm={() => remove(p)} testId={`admin-delete-programme-${p.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-6 text-sm text-[#756D69]">Aucun programme.</p>}
      </div>
      {editing !== undefined && (
        <ProgrammeFormDialog programme={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
};
